import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { Commission } from 'types';
import { URL } from 'api/constants';

import { apiQuery } from '../../query';

import {
  Cart,
  PaidCartCompletion,
  ResetResponse,
  CartAddRequest,
  ResetCartRequest,
  CartRemoveRequest,
  CartUpdateRequest,
  UnlockCartsRequest,
  UpdateDeviceRequest,
} from './types';

export const tagTypes = ['Cart'];

export const cartApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'cartApi',
  endpoints: (build) => ({
    managerCompletionAlerts: build.query<Array<{id: string; eventId: number; eventName: string; sessionName: string; isCurrentSession: boolean; paidAt?: string | null; paidAmountCents?: number}>, void>({
      providesTags: tagTypes,
      query: () => ({ method: 'get', url: URL.CART.split('/event/')[0] + '/manager-completion-alerts' }),
    }),
    needsCompletion: build.query<PaidCartCompletion[], string>({
      providesTags: tagTypes,
      query: id => ({ method: 'get', url: `${URL.CART.replace(':id', id)}/needs-completion` }),
    }),
    resumePaidCart: build.mutation<PaidCartCompletion, { eventId: string; cartId: number; approvalPassword: string }>({
      invalidatesTags: tagTypes,
      query: ({eventId,cartId,approvalPassword}) => ({ method: 'post',
        url: `${URL.CART.replace(':id', eventId)}/needs-completion/${cartId}/resume`, data: { approvalPassword } }),
    }),
    savePaidCartReps: build.mutation<{cartId: number; commissions: Commission[]}, { eventId: string; cartId: number; approvalId: string; commissions: Commission[] }>({
      invalidatesTags: tagTypes,
      query: ({eventId,cartId,...data}) => ({ method: 'post',
        url: `${URL.CART.replace(':id', eventId)}/needs-completion/${cartId}/reps`, data }),
    }),
    details: build.query<Cart, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.CART.replace(':id', id),
      }),
    }),

    add: build.mutation<Cart, CartAddRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.CART_ADD.replace(':id', String(id)),
      }),
    }),

    unlockCarts: build.mutation<Cart[], UnlockCartsRequest>({
      invalidatesTags: tagTypes,
      query: ({ userId }) => ({
        method: 'post',
        url: URL.UNLOCK_CARTS.replace(':userId', String(userId)),
      }),
    }),

    reset: build.mutation<ResetResponse, ResetCartRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.CART_RESET.replace(':id', id),
      }),
    }),

    updateDevice: build.mutation<Cart, UpdateDeviceRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.CART.replace(':id', String(id)),
      }),
    }),

    delete: build.mutation<Cart, CartRemoveRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, cartId }) => ({
        method: 'delete',
        url: URL.CART_REMOVE_ITEM.replace(':id', String(id)).replace(
          ':cartId',
          String(cartId)
        ),
      }),
    }),

    update: build.mutation<Cart, CartUpdateRequest>({
      invalidatesTags: tagTypes,
      query: ({ id, cartItemId, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.CART_UPDATE, {
          id: String(id),
          cartItemId: String(cartItemId),
        }),
      }),
    }),
  }),
});

export type { Cart };
export const {
  useManagerCompletionAlertsQuery,
  useSavePaidCartRepsMutation,
  useNeedsCompletionQuery,
  useResumePaidCartMutation,
  useAddMutation,
  useDetailsQuery,
  useResetMutation,
  useDeleteMutation,
  useUpdateMutation,
  useUnlockCartsMutation,
  useUpdateDeviceMutation,
} = cartApi;
